import { useTranslation } from 'react-i18next';
import styles from './GoalsLimitsTable.module.scss';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useFundContext from '@/contexts/Fund/useFundContext';
import ClassificationGoalLimitsDtoIn from '@/models/ClassificationGoalLimitsDtoIn';

type GoalRow = ClassificationGoalLimitsDtoIn & { limitText: string | null };

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('he-IL').format(value) + ' ₪';

const GoalsLimitsTable = ({ goalId }: { goalId: number }) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const url = currentFund ? `funds/${currentFund.id}/goals/${goalId}/classification-goal-limits` : '';
  const { data: classificationGoalLimitsData } = useGetAuthApi<ClassificationGoalLimitsDtoIn[]>(url);

  const formatLimit = (limits: ClassificationGoalLimitsDtoIn['limits']) => {
    if (limits.personalLimit !== null) {
      return t('personalGoalLimitDescription', { amount: formatCurrency(limits.personalLimit) });
    }
    if (limits.yearlyLimit !== null) {
      return t('yearlyGoalLimitDescription', { amount: formatCurrency(limits.yearlyLimit) });
    }
    if (limits.limitPercentage !== null) {
      return t('percentageGoalLimitDescription', { percentage: limits.limitPercentage });
    }
    if (limits.monthlyLimitFamily !== null && limits.monthlyLimitPerson !== null) {
      return t('goalLimitDescription', {
        familyAmount: formatCurrency(limits.monthlyLimitFamily),
        personAmount: formatCurrency(limits.monthlyLimitPerson),
      });
    }
    if (limits.monthlyLimitFamily !== null) {
      return t('monthlyFamilyGoalLimitDescription', { amount: formatCurrency(limits.monthlyLimitFamily) });
    }
    if (limits.monthlyLimitPerson !== null) {
      return t('monthlyPersonGoalLimitDescription', { amount: formatCurrency(limits.monthlyLimitPerson) });
    }
    return null;
  };

  const data: GoalRow[] = (classificationGoalLimitsData ?? []).map((g) => ({
    ...g,
    limitText: formatLimit(g.limits),
  }));

  if (classificationGoalLimitsData === undefined) return null;

  if (data.every((row) => row.limitText === null)) {
    return <p className={styles.emptyNote}>{t('goalHasNoLimits')}</p>;
  }

  return (
    <div className={styles.container}>
      <table className={`body-lr-regular ${styles.table}`}>
        <thead className={`body-md-semibold ${styles.thead}`}>
          <tr>
            <th className={`${styles.th} ${styles.projectCol}`}>{t('project')}</th>
            <th className={`${styles.th} ${styles.limitCol}`}>{t('limit')}</th>
          </tr>
        </thead>
        <tbody className={styles.tbody}>
          {data.map((row, idx) => (
            <tr key={idx} className={styles.tr}>
              <td className={`${styles.td} ${styles.projectCol}`}>
                <span className={`${styles.projectCell}`}>{t(row.classification.name)}</span>
              </td>
              <td className={`${styles.td} ${styles.limitCol}`}>
                <span>{row.limitText ?? '—'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default GoalsLimitsTable;
